// Campus baseline metrics — computed from building geometry + tree/wood polygons
// + published Thai benchmarks. The point: defensible NUMBERS we can show the
// client BEFORE they hand over any meter/booking data. Everything here is a
// MODELED, pre-meter ESTIMATE — the UI labels it as such (CLAUDE.md §11.4/§12).
//
// Sources (see docs/CAMPUS-METRICS.md for the full methodology + error bars):
//  - Energy Use Intensity (EUI, kWh/m²/yr): Thailand DEDE BEC / EPPO 20-Year EEDP.
//    Academic uses a MEASURED band (~150–220) anchored on CU-BEMS Chamchuri 5,
//    NOT the code design target of 102.
//  - Grid emission factor 0.4750 kgCO2e/kWh: TGO (Thailand Greenhouse Gas Mgmt
//    Organization) Scope-2, effective 2026-01-01.
//  - Tree sequestration 1.03 kgCO2/m²/yr canopy: Nowak & Crane 2002 / i-Tree.
//  - Aggregate confidence ±30–40% — presented as a band, never a single false-precise figure.

import type { Feature, FeatureCollection, Polygon, MultiPolygon, Position } from "geojson";

const EARTH_R = 6378137; // WGS84 metres
const toRad = (d: number) => (d * Math.PI) / 180;

/** Geodesic area (m²) of a single lng/lat ring — standard spherical-excess formula. */
function ringAreaSqm(ring: Position[]): number {
  const n = ring.length;
  if (n < 3) return 0;
  let total = 0;
  for (let i = 0; i < n; i++) {
    const [lng1, lat1] = ring[i];
    const [lng2, lat2] = ring[(i + 1) % n];
    total += toRad(lng2 - lng1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
  }
  return Math.abs((total * EARTH_R * EARTH_R) / 2);
}

/** Area (m²) of a Polygon/MultiPolygon, outer rings minus holes. */
export function geomAreaSqm(geom: Polygon | MultiPolygon | null | undefined): number {
  if (!geom) return 0;
  const polys = geom.type === "Polygon" ? [geom.coordinates] : geom.coordinates;
  let area = 0;
  for (const poly of polys) {
    if (!poly.length) continue;
    area += ringAreaSqm(poly[0]);
    for (let h = 1; h < poly.length; h++) area -= ringAreaSqm(poly[h]); // holes
  }
  return area;
}

function sumAreas(fc?: FeatureCollection<Polygon | MultiPolygon, unknown> | null): number {
  if (!fc?.features) return 0;
  let a = 0;
  for (const f of fc.features) a += geomAreaSqm(f.geometry as Polygon | MultiPolygon);
  return a;
}

// ── Benchmarks ────────────────────────────────────────────────────────────
const GRID_FACTOR = 0.4750; // kgCO2e/kWh — TGO Scope-2, 2026
const SEQUESTRATION = 1.03; // kgCO2/m² canopy/yr — i-Tree
const FLOOR_TO_FLOOR = 3.0; // m, tropical institutional standard

/**
 * Measured EUI band the model assumes for academic buildings (kWh/m²/yr) —
 * anchored on CU-BEMS Chamchuri 5 (see lib/cubems.ts). Exported so the UI can
 * print the measured-vs-model calibration note from ONE source of truth.
 */
export const ACADEMIC_EUI_BAND = { low: 150, high: 220 } as const;

// EUI by OSM `building` tag (kWh/m²/yr). Academic kept at the CONSERVATIVE end
// of the measured 150–220 band (CU-BEMS anchor) so the campus total doesn't
// over-claim; office/retail/hospital use the DEDE/EPPO published references.
const EUI: Record<string, number> = {
  office: 219, commercial: 308, retail: 370, supermarket: 370,
  hotel: 271, hospital: 244, clinic: 244,
  apartments: 256, residential: 256, dormitory: 256, house: 180,
  university: 170, school: 170, college: 170, academic: 170,
  yes: 170, // untyped → treat as academic (campus-dominant use)
};
const DEFAULT_EUI = 170;

// Default storeys when neither `levels` nor `height` is present. Deliberately
// CONSERVATIVE (≤ a flat 4-storey assumption) so an unknown building can't
// inflate the floor-area aggregate beyond the defensible upper bound.
const DEFAULT_LEVELS: Record<string, number> = {
  university: 4, school: 3, college: 4, academic: 4, yes: 3,
  hospital: 6, clinic: 3, dormitory: 5, apartments: 5, residential: 4,
  house: 2, office: 5, commercial: 4, retail: 2, hotel: 6,
};

export interface BuildingProps {
  id?: string; building?: string | null;
  levels?: number | null; height?: number | null;
}

function levelsFor(p: BuildingProps): { levels: number; known: boolean } {
  if (p.levels != null && p.levels > 0) return { levels: p.levels, known: true };
  if (p.height != null && p.height > 0) return { levels: Math.max(1, Math.round(p.height / FLOOR_TO_FLOOR)), known: false };
  const t = (p.building ?? "yes").toLowerCase();
  return { levels: DEFAULT_LEVELS[t] ?? 4, known: false };
}

const euiFor = (t?: string | null) => EUI[(t ?? "yes").toLowerCase()] ?? DEFAULT_EUI;

export interface CampusMetrics {
  campusAreaSqm: number;
  campusAreaRai: number;
  footprintSqm: number;
  gfaSqm: number;        // estimated gross floor area (with level inference)
  gfaKnownSqm: number;   // lower bound — only buildings with explicit `levels`
  buildings: number;
  annualEnergyGWh: number;
  carbonCentral: number; // tCO2e/yr, operational Scope-2
  carbonLow: number;
  carbonHigh: number;
  greenCoverPct: number; // wood polygons / campus area
  treeCount: number;
  sequestrationTonnes: number;
  gridFactor: number;
}

/** Compute the full baseline from the loaded GeoJSON. Pure; safe with partial data. */
export function computeCampusMetrics(
  buildings: FeatureCollection<Polygon | MultiPolygon, BuildingProps> | null | undefined,
  campus: FeatureCollection<Polygon | MultiPolygon, unknown> | null | undefined,
  wood: FeatureCollection<Polygon | MultiPolygon, unknown> | null | undefined,
  treeCount: number,
): CampusMetrics {
  const campusAreaSqm = sumAreas(campus);
  const woodAreaSqm = sumAreas(wood);

  let footprintSqm = 0, gfaSqm = 0, gfaKnownSqm = 0, annualKWh = 0, n = 0;
  for (const f of buildings?.features ?? []) {
    const p = f.properties ?? {};
    const fa = geomAreaSqm(f.geometry as Polygon | MultiPolygon);
    if (fa <= 0) continue;
    const { levels, known } = levelsFor(p);
    const gfa = fa * levels;
    footprintSqm += fa;
    gfaSqm += gfa;
    if (known) gfaKnownSqm += gfa;
    annualKWh += gfa * euiFor(p.building);
    n++;
  }

  const annualEnergyGWh = annualKWh / 1_000_000; // kWh → GWh
  const carbonCentral = (annualKWh * GRID_FACTOR) / 1000; // kg → tonnes
  return {
    campusAreaSqm,
    campusAreaRai: campusAreaSqm / 1600, // 1 rai = 1600 m²
    footprintSqm,
    gfaSqm,
    gfaKnownSqm,
    buildings: n,
    annualEnergyGWh,
    carbonCentral,
    carbonLow: carbonCentral * 0.65,  // ±35% honest band (aggregate ±30–40%)
    carbonHigh: carbonCentral * 1.35,
    greenCoverPct: campusAreaSqm > 0 ? (woodAreaSqm / campusAreaSqm) * 100 : 0,
    treeCount,
    sequestrationTonnes: (woodAreaSqm * SEQUESTRATION) / 1000,
    gridFactor: GRID_FACTOR,
  };
}
